// Fase 15A — SuperAdmin Dashboard (punto 6): contatori piattaforma, alert di
// configurazione e health engine. Mai dati ESG aziendali (punto 3).
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, AlertTriangle, AlertOctagon, CheckCircle2, Activity } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const CARDS = [
  { key: "organizations_active", label: "Studi attivi", to: "/admin/platform/studi", tid: "pf-orgs" },
  { key: "consultants", label: "Consulenti", to: "/admin/platform/consulenti", tid: "pf-consultants" },
  { key: "companies", label: "Aziende clienti", to: "/admin/platform/aziende", tid: "pf-companies" },
  { key: "users_active", label: "Utenti attivi", to: "/admin/platform/utenti", tid: "pf-users" },
  { key: "invitations_pending", label: "Inviti pendenti", to: "/admin/platform/inviti", tid: "pf-invites" },
  { key: "accounts_suspended", label: "Account sospesi", to: "/admin/platform/utenti", tid: "pf-suspended" },
  { key: "data_rooms_active", label: "Data Room attive", to: null, tid: "pf-rooms" },
  { key: "frameworks_installed", label: "Framework installati", to: "/admin/frameworks", tid: "pf-frameworks" },
];

// FASE 2: contatori operativi aggregati della piattaforma (mai dati di una singola azienda).
const OPS_CARDS = [
  { key: "companies_active", label: "Aziende attive", tid: "pf-comp-active" },
  { key: "companies_inactive", label: "Aziende non attive", tid: "pf-comp-inactive" },
  { key: "questionnaires_compiling", label: "Questionari in compilazione", tid: "pf-q-compiling" },
  { key: "questionnaires_submitted", label: "Questionari inviati", tid: "pf-q-submitted" },
  { key: "values_warning", label: "Dati con WARNING", tid: "pf-v-warning", warn: true },
  { key: "values_inconsistent", label: "Dati INCONSISTENT", tid: "pf-v-inconsistent", danger: true },
  { key: "score_runs", label: "ScoreRun generati", tid: "pf-scoreruns" },
  { key: "reports_generated", label: "Report generati", tid: "pf-reports" },
  { key: "jobtimehr_connections_active", label: "Integrazioni JOBTIMEHR attive", to: "/admin/platform/integrazioni", tid: "pf-jt-conn" },
  { key: "jobtimehr_syncs_failed", label: "Sincronizzazioni fallite", to: "/admin/platform/integrazioni", tid: "pf-jt-failed", danger: true },
];

export default function PlatformDashboard() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [mon, setMon] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, h, m] = await Promise.all([
        api.get("/admin/platform/dashboard"),
        api.get("/admin/platform/health"),
        api.get("/admin/platform/monitoring"),
      ]);
      setData(d.data);
      setHealth(h.data);
      setMon(m.data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-red-700" data-testid="platform-dash-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="platform-dash-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="platform-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <LayoutDashboard className="w-7 h-7 text-green-800" aria-hidden="true" />
          SuperAdmin Console
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Amministrazione centrale della piattaforma: studi, consulenti, aziende, utenti,
          ruoli, moduli e inviti. I contenuti ESG delle aziende non sono visibili da qui.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {CARDS.map((c) => (
          <Card key={c.tid} className="border-slate-200" data-testid={c.tid}>
            <CardContent className="pt-4">
              <p className="text-[11px] text-slate-500">{c.label}</p>
              <p className="text-2xl font-bold text-slate-900">{data[c.key]}</p>
              {c.to && <Link to={c.to} className="text-[11px] text-green-700 hover:underline">Apri →</Link>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="platform-ops-cards">
        {OPS_CARDS.map((c) => (
          <Card key={c.tid} className={`border-slate-200 ${c.danger && data[c.key] > 0 ? "border-red-300 bg-red-50" : ""} ${c.warn && data[c.key] > 0 ? "border-amber-300 bg-amber-50" : ""}`}
            data-testid={c.tid}>
            <CardContent className="pt-4">
              <p className="text-[11px] text-slate-500">{c.label}</p>
              <p className="text-2xl font-bold text-slate-900">{data[c.key] ?? "—"}</p>
              {c.to && <Link to={c.to} className="text-[11px] text-green-700 hover:underline">Apri →</Link>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200" data-testid="platform-alerts-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" aria-hidden="true" /> Alert amministrativi
          </CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Aziende senza consulente</span>
              <strong data-testid="alert-no-consultant">{data.alerts.companies_without_consultant}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Inviti scaduti da reinviare</span>
              <strong data-testid="alert-expired-invites">{data.alerts.invitations_expired}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Consulenti senza assegnazioni</span>
              <strong data-testid="alert-idle-consultants">{data.alerts.consultants_without_assignments}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Incidenti aperti</span>
              <strong>{data.open_incidents}</strong></div>
            <p className="text-[11px] text-slate-500 pt-2 border-t border-slate-100">
              Ultimo backup: {data.last_backup ? data.last_backup.started_at.slice(0, 16).replace("T", " ") : "mai"}
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="platform-health-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            {health?.status === "OK"
              ? <CheckCircle2 className="w-4 h-4 text-green-700" aria-hidden="true" />
              : <AlertOctagon className="w-4 h-4 text-red-600" aria-hidden="true" />}
            Configuration Health
            <Badge variant="outline" className={health?.status === "OK"
              ? "bg-green-50 text-green-800 border-green-300"
              : health?.status === "ERROR" ? "bg-red-50 text-red-800 border-red-300"
              : "bg-amber-50 text-amber-800 border-amber-300"}
              data-testid="health-status">{health?.status}</Badge>
          </CardTitle></CardHeader>
          <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
            {health?.checks.length === 0 && (
              <p className="text-xs text-slate-500">Nessun problema di configurazione rilevato.</p>)}
            {health?.checks.slice(0, 20).map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-xs border-b border-slate-100 pb-1.5"
                data-testid={`health-check-${i}`}>
                <Badge variant="outline" className={c.status === "ERROR"
                  ? "bg-red-50 text-red-800 border-red-300 text-[9px]"
                  : "bg-amber-50 text-amber-800 border-amber-300 text-[9px]"}>{c.status}</Badge>
                <span className="font-medium text-slate-800 truncate">{c.entity}</span>
                <span className="text-slate-500 truncate">{c.message}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="platform-monitoring-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-green-700" aria-hidden="true" /> Salute piattaforma
            <Badge variant="outline" className={mon?.ready
              ? "bg-green-50 text-green-800 border-green-300"
              : "bg-red-50 text-red-800 border-red-300"}
              data-testid="monitoring-ready-badge">{mon ? (mon.ready ? "READY" : "NOT READY") : "…"}</Badge>
          </CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-sm max-h-64 overflow-y-auto">
            <div className="flex justify-between">
              <span className="text-slate-600">Errori 5xx (ultime 24h)</span>
              <strong data-testid="monitoring-errors-24h">{mon?.errors_24h ?? 0}</strong>
            </div>
            {mon && mon.recent_errors.length === 0 && (
              <p className="text-xs text-slate-500">Nessun errore 5xx nelle ultime 24 ore.</p>)}
            {mon?.recent_errors.slice(0, 10).map((e) => (
              <div key={e.id} className="text-xs border-b border-slate-100 pb-1"
                data-testid={`monitoring-error-${e.id}`}>
                <span className="font-medium text-red-700">{e.status_code}</span>{" "}
                <span className="text-slate-700">{e.method} {e.path}</span>
                <span className="block text-[10px] text-slate-400">
                  {e.timestamp?.slice(0, 19).replace("T", " ")} · req {e.request_id || "—"}
                </span>
              </div>
            ))}
            <p className="text-[11px] text-slate-500 pt-1">
              Alert email automatico ai SuperAdmin se il DB non risponde o ≥10 errori 5xx/ora
              (cron ogni 15 min).
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
